import { FC, useEffect, useState } from 'react'

import { useGetUserByToken } from '@/helpers/useGetUserByToken'
import {
    TQuestionAnswer,
    TQuiz,
    useAddQuizAnswerMutation,
    useLazyGetQuizAccessQuery
} from '@/redux/services/webinarsApi'
import Finish from './Finish'
import Footer from './Footer'
import { Container, Intro, Question, QuestionHeader, Title } from './Quiz.components'
import { getFinishDescription, getTime, useDisableScroll } from './Quiz.model'

type QuizPopupProps = {
    data: TQuiz
    onClose: () => void
}

export type TQuizStage = 'not_started' | 'in_progress' | 'finalized' | 'time_out'
export type TExcludeQuestionAnswer = Omit<TQuestionAnswer, 'author'>
export type TResultStatus = 'idle' | 'success' | 'failed'
export type TResult = {
    status: TResultStatus
    correctCount: number
}

const QuizPopup: FC<QuizPopupProps> = ({ data, onClose }) => {
    const [refetchQuizAccess] = useLazyGetQuizAccessQuery()
    const [addQuizAnswer, { isLoading }] = useAddQuizAnswerMutation()
    const [stage, setStage] = useState<TQuizStage>('not_started')
    const [currentQuestion, setCurrentQuestion] = useState(1)
    const questionsCount = data?.questions.length
    const { time, minutes, seconds } = getTime(data?.timeToAnswer)
    const passingScore = Math.round(questionsCount * 0.9)
    const progress = (currentQuestion / questionsCount) * 100
    const isFinished = stage === 'finalized' || stage === 'time_out'
    const user = useGetUserByToken()
    const [questionAnswers, setQuestionAnswers] = useState<TExcludeQuestionAnswer[]>(
        data.questions.map(({ id }) => ({ question: id, text: '', multi_text: [] }))
    )
    const [result, setResult] = useState<TResult>({
        status: 'idle',
        correctCount: 0
    })

    useDisableScroll()

    useEffect(() => {
        if (!['finalized', 'time_out'].includes(stage)) return

        sendResult()
    }, [stage])

    const handleNextQuestion = () => {
        if (currentQuestion === questionsCount) {
            setStage('finalized')
            return
        }

        setCurrentQuestion(currentQuestion + 1)
    }

    const handleCheckboxChange = (index: number) => (answer: TExcludeQuestionAnswer) => {
        setQuestionAnswers(prev => {
            const newAnswers = prev.filter(v => v.question !== answer.question)
            return [...newAnswers, answer]
        })
    }

    const getFinishTitle = () => {
        const titleByStage = {
            finalized: 'Тест завершен',
            time_out: 'Время вышло'
        }

        return titleByStage[stage] ?? null
    }

    const sendResult = async () => {
        try {
            const quizId = data.id
            const author = user?.id
            const result = await addQuizAnswer({
                author: author,
                quiz: quizId,
                question_answers: questionAnswers.map(v => ({ ...v, author: author }))
            }).unwrap()
            await refetchQuizAccess({ userId: user.id, quizId: quizId })
            const resultStatus = result.correct ? 'success' : 'failed'
            const correctCount = result.question_answers.filter(v => v.correct).length
            setResult({ status: resultStatus, correctCount })
        } catch (error) {
            
        }
    }

    if (isFinished) {
        return (
            <Container
                isFinish={isFinished}
                onClose={onClose}
            >
                <Finish
                    title={getFinishTitle()}
                    description={getFinishDescription(result.status, result.correctCount, questionsCount)}
                    isLoading={isLoading}
                    onClose={onClose}
                />
            </Container>
        )
    }

    return (
        <Container
            isFinish={isFinished}
            onClose={onClose}
        >
            <>
                <Title title={data.title} />
                {stage === 'in_progress' ? (
                    <>
                        <QuestionHeader
                            progress={progress}
                            currentQuestion={currentQuestion}
                            questionsCount={questionsCount}
                        />
                        <Question
                            key={currentQuestion}
                            question={data.questions[currentQuestion - 1]}
                            onCheckboxChange={handleCheckboxChange(currentQuestion - 1)}
                        />
                    </>
                ) : (
                    <Intro
                        passingScore={passingScore}
                        questionsCount={questionsCount}
                        minutes={minutes}
                        seconds={seconds}
                    />
                )}
                <Footer
                    time={time}
                    isStarted={stage === 'in_progress'}
                    setStage={setStage}
                    onNext={handleNextQuestion}
                />
            </>
        </Container>
    )
}

export default QuizPopup
